import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, PackageSearch, RefreshCw } from 'lucide-react';
import ProdutosAlmoxarifadosFiltradosTable from 'features/estoque/components/SubAbas/Components/ProdutosAlmoxarifadosFiltradosTable';
import { getCompanyContext } from 'utils/setCompanyContext';
import { usePermissions } from 'hooks/usePermissions';
import materialBaseInfraService from '../services/materialBaseInfraService';
import estoqueMinimosService from '../services/estoqueMinimosService';
import { contarProdutosConfigurados, indexarMinimos } from '../utils/estoqueMinimo';
import EstoqueMinimoModal from './EstoqueMinimoModal';

const panelClassName =
  'rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_16px_40px_rgba(15,23,42,0.10)]';

const extractProdutos = (response) => {
  if (Array.isArray(response?.data?.produtos_filtrados)) {
    return response.data.produtos_filtrados;
  }

  if (Array.isArray(response?.produtos_filtrados)) {
    return response.produtos_filtrados;
  }

  return [];
};

const getSelectedCompanyKey = () => {
  const context = getCompanyContext();
  const ids = Array.isArray(context?.ids) ? context.ids : [];
  return ids.length > 0 ? ids.map(String).join('|') : 'no-company';
};

const isAbortError = (error) => (
  error?.name === 'AbortError'
  || error?.name === 'CanceledError'
  || error?.code === 'ERR_CANCELED'
);

const isIxcUnavailable = (error) => error?.response?.data?.code === 'IXC_UNAVAILABLE';

const MaterialBaseInfraDashboard = ({ onReloadReady = null }) => {
  const { hasPermission } = usePermissions();
  // Sem a permissão de escrita o editor nem abre: deixar preencher para falhar no Salvar
  // descartaria o trabalho e avisaria no pior momento. A visualização do mínimo continua.
  const podeEditarMinimo = hasPermission('estoque.minimos.manage');
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [companyKey, setCompanyKey] = useState(() => getSelectedCompanyKey());
  const [reloadToken, setReloadToken] = useState(0);
  const [minimos, setMinimos] = useState([]);
  const [produtoEmEdicao, setProdutoEmEdicao] = useState(null);
  const [somenteAbaixo, setSomenteAbaixo] = useState(false);
  const requestIdRef = useRef(0);
  const pendingReloadRef = useRef(null);

  const carregarDados = useCallback(() => {
    requestIdRef.current += 1;
    setReloadToken((current) => current + 1);

    return new Promise((resolve) => {
      pendingReloadRef.current = {
        invalidatedRequestId: requestIdRef.current,
        resolve,
      };
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const invalidatedRequestId = requestIdRef.current;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let ignore = false;

    if (pendingReloadRef.current?.invalidatedRequestId === invalidatedRequestId) {
      pendingReloadRef.current.requestId = requestId;
    }

    const fetchProdutos = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await materialBaseInfraService.buscarProdutosFiltrados(controller.signal);

        if (ignore || controller.signal.aborted || requestId !== requestIdRef.current || companyKey !== getSelectedCompanyKey()) {
          return;
        }

        setProdutos(extractProdutos(response));
      } catch (error) {
        if (ignore || controller.signal.aborted || isAbortError(error) || requestId !== requestIdRef.current) {
          return;
        }

        setProdutos([]);
        setErrorMessage(isIxcUnavailable(error)
          ? 'O IXC da empresa selecionada está indisponível para a leitura de material base.'
          : (error?.response?.data?.message || error?.message || 'Não foi possível carregar o material base de infraestrutura.'));
      } finally {
        if (!ignore && !controller.signal.aborted && requestId === requestIdRef.current) {
          setLoading(false);
        }

        if (pendingReloadRef.current?.requestId === requestId) {
          const { resolve } = pendingReloadRef.current;
          pendingReloadRef.current = null;
          resolve();
        }
      }
    };

    fetchProdutos();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [companyKey, reloadToken]);

  // A configuração é local: recarrega ao trocar de empresa, não ao recarregar o saldo.
  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;

    // Limpa antes de buscar: os ids de produto só valem dentro da base da empresa, então manter a
    // configuração anterior na tela pintaria de vermelho o produto errado até a resposta chegar.
    setMinimos([]);

    estoqueMinimosService.buscarMinimos(controller.signal)
      .then((resultado) => {
        if (!ignore) {
          setMinimos(resultado);
        }
      })
      .catch((error) => {
        if (!ignore && !isAbortError(error)) {
          // Falha aqui não pode derrubar a tela de saldo: sem mínimos, ela volta ao que era.
          setMinimos([]);
        }
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [companyKey]);

  const indiceMinimos = useMemo(() => indexarMinimos(minimos), [minimos]);
  const produtosConfigurados = useMemo(() => contarProdutosConfigurados(minimos), [minimos]);
  const totalProdutos = useMemo(
    () => new Set(produtos.map((item) => item.id_produto)).size,
    [produtos],
  );

  // Colunas vêm do payload; o saldo exibido é o do produto em edição, para o mínimo ser definido
  // olhando o número real em vez de chute.
  const almoxarifadosDoProduto = useMemo(() => {
    const porId = new Map();

    produtos.forEach((item) => {
      if (item.id_almox === null || item.id_almox === undefined) {
        return;
      }

      if (!porId.has(item.id_almox)) {
        porId.set(item.id_almox, {
          id_almox: item.id_almox,
          descricao: item.Almoxarifado,
          saldo: 0,
        });
      }

      if (produtoEmEdicao && item.id_produto === produtoEmEdicao.id_produto) {
        porId.get(item.id_almox).saldo = Number(item.Saldo || 0);
      }
    });

    return [...porId.values()].sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
  }, [produtos, produtoEmEdicao]);

  const salvarMinimo = useCallback(async (payload) => {
    const atualizados = await estoqueMinimosService.salvarProduto(
      produtoEmEdicao.id_produto,
      payload,
    );
    setMinimos(atualizados);
  }, [produtoEmEdicao]);

  useEffect(() => {
    if (!onReloadReady) {
      return undefined;
    }

    onReloadReady(carregarDados);

    return () => onReloadReady(null);
  }, [carregarDados, onReloadReady]);

  useEffect(() => {
    const handleCompanyChange = () => {
      setCompanyKey(getSelectedCompanyKey());
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarDados]);

  return (
    <div className="mt-4 space-y-4">
      <div className={`${panelClassName} flex flex-col gap-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--text-muted)]">
              <PackageSearch className="h-4 w-4 text-[color:var(--accent)]" />
              Material base de infraestrutura
            </div>
            <h2 className="mt-3 text-lg font-semibold text-[color:var(--text-primary)]">
              Produtos por almoxarifado
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-[color:var(--text-muted)]">
              Visão de saldo por produto e almoxarifado, com busca, KPIs e distribuição no mesmo padrão da Diretoria Operacional.
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 sm:items-end">
            <button
              type="button"
              onClick={() => setSomenteAbaixo((atual) => !atual)}
              aria-pressed={somenteAbaixo}
              className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                somenteAbaixo
                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-500'
                  : 'border-[color:var(--header-control-border-subtle)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
              }`}
            >
              {somenteAbaixo ? 'Mostrando abaixo do mínimo' : 'Somente abaixo do mínimo'}
            </button>
            {/* Sem este indicador, lista vazia no filtro é ambígua: pode ser "tudo em ordem"
                ou "ninguém configurou ainda". */}
            <p className="text-[11px] text-[color:var(--text-muted)]">
              {`${produtosConfigurados.toLocaleString('pt-BR')} de ${totalProdutos.toLocaleString('pt-BR')} produtos com mínimo definido`}
            </p>
            {/* Linha não clicável sem explicação vira "está quebrado". */}
            {!podeEditarMinimo ? (
              <p className="text-[11px] text-[color:var(--text-muted)]">
                Alterar o mínimo exige permissão específica.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {errorMessage ? (
        <div className={`${panelClassName} flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}>
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-400" />
            <div>
              <p className="text-sm font-semibold text-[color:var(--text-primary)]">Falha ao carregar material base</p>
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">{errorMessage}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => carregarDados()}
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] px-4 py-2 text-sm font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)]"
          >
            <RefreshCw className="h-4 w-4 text-[color:var(--accent)]" />
            Tentar novamente
          </button>
        </div>
      ) : null}

      <ProdutosAlmoxarifadosFiltradosTable
        data={produtos}
        loading={loading}
        minimos={indiceMinimos}
        onEditarMinimo={podeEditarMinimo ? setProdutoEmEdicao : null}
        somenteAbaixoDoMinimo={somenteAbaixo}
      />

      <EstoqueMinimoModal
        produto={produtoEmEdicao}
        almoxarifados={almoxarifadosDoProduto}
        minimos={minimos}
        onSalvar={salvarMinimo}
        onClose={() => setProdutoEmEdicao(null)}
      />
    </div>
  );
};

export default MaterialBaseInfraDashboard;
